<template>
  <VAppBar color="white" :elevation="0" height="70">
    <div class="header">
      <RouterLink class="edaywork-logo" to="/">
        <VImg alt="Logo" class="shrink mr-2" cover :src="logo" transition="scale-transition" width="90" />
      </RouterLink>

      <div v-if="userStore.user" class="profile">
        <div>
          Bonjour
          <span class="bold">{{ userStore.user.name }}</span>
        </div>

        <div v-if="userStore.user.roles.isExec" class="annual-counter">
          <div class="label">Compteur annuel des jours travaillés</div>
          <div :class="{ 'red--text': isPeriodOverWorked }" class="bold">
            {{ userStore.user.totalWorkedDays ?? 'information manquante' }} /
            {{
              userStore.user.flatRateWork && userStore.user.flatRateWork >= 0
                ? `${userStore.user.flatRateWork}`
                : 'information manquante'
            }}
          </div>
        </div>
      </div>

      <ToastList />
    </div>

    <template #extension>
      <div class="extension">
        <VDivider class="divider" />

        <VTabs
          v-if="!errorStore.hasFatalError"
          align-tabs="center"
          selected-class="active-route"
          height="36px"
          hide-slider
        >
          <VTab v-for="(route, index) in pages" :key="index" :to="{ path: route.path }" :ripple="false" rounded="lg">
            {{ route.title }}
          </VTab>
        </VTabs>

        <VFabTransition v-if="rootStore.isMobilePlatform && rootStore.canHideMonthlySummary">
          <VBtn
            position="absolute"
            location="left"
            size="small"
            icon="mdi-calendar-multiple-check"
            class="month-summary-button"
            @click="toggleMonthlySummaryMenu"
          />
        </VFabTransition>
      </div>
    </template>
  </VAppBar>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import ToastList from '@/components/ToastList.vue';
import routesConstants from '@/constants/routesConstants.ts';
import blueLogo from '../assets/edaywork-logo-default.png';
import mochaLogo from '../assets/edaywork-logo-mocha.png';
import purpleLogo from '../assets/edaywork-logo-purple.png';
import useErrorsStore from '@/stores/errorStore.ts';
import useUserStore from '@/stores/userStore.ts';
import type { Route } from '@/types/routesTypes.ts';
import useRootStore from '@/stores/rootStore.ts';

const route = useRoute();
const userStore = useUserStore();
const rootStore = useRootStore();

const errorStore = useErrorsStore();

const roleFromRoute = computed(() => (route.path || '').split('/')[1] || '');

const logo = computed(() => {
  switch (roleFromRoute.value) {
    case 'executive':
      return blueLogo;
    case 'manager':
      return mochaLogo;
    case 'hr':
      return purpleLogo;
    default:
      return blueLogo;
  }
});

const pages = computed(() => {
  const list: Route[] = [];

  if (userStore.user.roles.isExec) {
    list.push(routesConstants.EXECUTIVE);
  }
  if (userStore.user.roles.isManager) {
    list.push(routesConstants.MANAGER);
  }
  if (userStore.user.roles.isHR) {
    list.push(routesConstants.HR);
  }

  return list;
});

const isPeriodOverWorked = computed(() => {
  if (userStore.user.flatRateWork && userStore.user.totalWorkedDays) {
    return userStore.user.totalWorkedDays >= 0 && userStore.user.flatRateWork >= 0
      ? userStore.user.totalWorkedDays > userStore.user.flatRateWork
      : false;
  }

  return false;
});

function toggleMonthlySummaryMenu() {
  rootStore.toggleMonthlySummary();
}
</script>

<style lang="scss" scoped>
@use '@/styles/variables';
@use '@/styles/colors';

.header {
  display: flex;
  align-items: center;
  width: 100%;

  .profile {
    position: absolute;
    text-align: center;
    top: 5px;
    max-width: 200px;
    right: 10px;

    .annual-counter {
      border: 2px solid;
      border-radius: 10px;

      .label {
        font-size: 0.875em;
      }

      .name {
        font-size: 1em;
      }

      .count {
        font-size: 1.125em;
        font-weight: bold;
      }
    }
  }
}

.divider {
  width: 30%;
  max-width: 60%;
  margin: 8px auto;
}

.active-route {
  background-color: rgb(var(--v-theme-primary));
  color: colors.$white !important;
}

.v-tab {
  text-transform: none;
}

.theme--light.v-tabs .v-tab:focus::before,
.theme--light.v-tabs .v-tab:hover::before {
  opacity: 0;
}

.extension {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
}

.month-summary-button {
  background-color: rgb(var(--v-theme-primary));
  color: colors.$white;
  margin-left: 8px;
}

@media (min-width: variables.$sm) {
  .header {
    justify-content: center;

    .edaywork-logo {
      margin-left: 8px;
    }
  }
}

@media (max-width: variables.$sm) {
  .extension {
    width: 100%;
    margin: 0;
  }
  .header {
    width: 100%;

    .profile {
      left: inherit;
      right: 10px;
    }
  }
}
</style>
